import {
  MenuItem,
  OuiFooterComponent,
  OuiSymbolComponent,
} from '@aiz-gl/omni-ui';
import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { css } from '@emotion/css';
import { filter } from 'rxjs';
import { DownloadsButtonComponent } from './components/downloads-button/downloads-button.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { SpinnerComponent } from './components/spinner/spinner';
import { ToastComponent } from './components/toast/toast.component';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { APP_SYSTEM_NAME, TEXT } from './constants/text';
import { AuthService } from './services/auth/auth.service';
import { FeatureAccessService } from './services/feature/feature-access.service';
import { SpinerService } from './services/spinner/spinner-service';
import { ToastService } from './services/toast/toast.service';
import { UserDccsService } from './services/user-dccs/user-dccs.service';
import { UserRoleService } from './services/user-role/user-role-service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SpinnerComponent,
    SidebarComponent,
    DropdownMenuComponent,
    OuiSymbolComponent,
    OuiFooterComponent,
    ToastComponent,
    UserMenuComponent,
    DownloadsButtonComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit {
  get selectedLabel(): string {
    return this.userDccService.getUserAssignedDcc() ?? '';
  }

  selectedDccLabel = '';
  sidebarOpen = false;
  sidebarCollapsed =
    localStorage.getItem('omniflow:sidebarCollapsed') === 'true';

  private readonly desktopMq = window.matchMedia('(min-width: 48rem)');

  toggleSidebar(): void {
    if (this.desktopMq.matches) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem(
        'omniflow:sidebarCollapsed',
        String(this.sidebarCollapsed),
      );
    } else {
      this.sidebarOpen = !this.sidebarOpen;
    }
  }

  private readonly router = inject(Router);
  private readonly userDccsService = inject(UserDccsService);
  readonly userDccService = inject(UserDccsService);
  private readonly userRoleService = inject(UserRoleService);
  readonly authService = inject(AuthService);
  readonly spinner = inject(SpinerService);
  private readonly featureAccess = inject(FeatureAccessService);
  private readonly toast = inject(ToastService);

  title = 'omniflow';
  warehouseMenuItems!: MenuItem[];

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.spinner.hideIfActive();
      }
    });
  }

  ngOnInit() {
    this.authService.oktaState$
      .pipe(filter((state) => state.isAuthenticated === true))
      .subscribe((authState) => {
        const email = authState.idToken?.claims?.email;
        if (email) {
          this.userDccsService.userAllocatedDccsLoading.set(true);
          this.userDccsService
            .fetchUserAllocatedDccs({
              email_id: email,
              system: APP_SYSTEM_NAME,
            })
            .subscribe((result) => {
              this.userDccsService.userAllocatedDccs.set(result);
              this.userDccsService.userAllocatedDccsError.set(null);
              this.userDccsService.userAllocatedDccsLoading.set(false);
            });
          this.userRoleService.fetchAlldDccs().subscribe();
        }
      });
  }

  private readonly setSelectedDccLabelEffect = effect(() => {
    const dccs = this.userDccsService.userAllocatedDccs();
    if (dccs && dccs.data && dccs.data.length > 0 && !this.selectedDccLabel) {
      setTimeout(() => {
        this.selectedDccLabel = dccs.data[0].dcc_code;
      });
    }
  });

  shadowClass = css({
    boxShadow:
      'inset 0  15px 10px -13px rgba(0, 0, 0, 0.2),' +
      'inset 0 -15px 10px -13px rgba(0, 0, 0, 0.2)',
  });

  dccCodeMenuItems = computed<MenuItem[]>(() => {
    const dccs = this.userDccsService.userAllocatedDccs();
    return dccs?.data.map((item) => ({ label: item.dcc_code })) ?? [];
  });

  async onDccMenuItemClick(event: MenuItem) {
    const dccCode = event.label;
    if (!dccCode) {
      return;
    }

    this.userDccService.setUserAssignedDcc(dccCode);

    const policiesLoaded = this.authService.shouldRefreshPoliciesForDcc(dccCode)
      ? await this.authService.refreshPoliciesForDcc(dccCode)
      : true;

    if (!policiesLoaded && !this.authService.isPolicyAccessDenied()) {
      return;
    }

    // If the policy genuinely failed to load, let the guard surface the
    // policy-denied page (navigating to any DCC route triggers it).
    if (this.authService.isPolicyAccessDenied()) {
      await this.router.navigate(['/dock-menu', dccCode, 'search-dock']);
      return;
    }

    // Stay on the current page if the newly selected DCC can access it;
    // otherwise fall back to the first accessible feature for that DCC.
    const currentSegments = this.router.url
      .split('?')[0]
      .split('/')
      .filter(Boolean);
    const currentRoute =
      currentSegments[0] === 'dock-menu' ? currentSegments[2] : undefined;

    const landingRoute = await this.featureAccess.resolveDccLandingRoute(
      dccCode,
      currentRoute,
    );

    if (!landingRoute) {
      this.toast.error(TEXT.dashboard.noAccessibleFeatures);
      await this.router.navigate(['/dashboard']);
      return;
    }

    await this.router.navigate(['/dock-menu', dccCode, landingRoute]);
  }

  get showHeaderMenus(): boolean {
    return this.router.url !== '/login';
  }

  get isLoginPage(): boolean {
    return this.router.url === '/' || this.router.url.startsWith('/login');
  }

  readonly currentYear = new Date().getFullYear();

  get isDashboardPage(): boolean {
    return this.router.url.startsWith('/dashboard');
  }

  get isAdminRoute(): boolean {
    return (
      this.router.url.includes('/user-role') ||
      this.router.url.includes('/dcc-configuration')
    );
  }

  get isHeaderVisible(): boolean {
    return !this.isLoginPage;
  }
}
